/**
 * Grafik kabuğu — her grafik bunun içinde yaşar.
 *
 * Sağladıkları (dataviz erişilebilirlik geçişi):
 *  · Başlık + alt başlık: TEK seri olduğunda başlığın kendisi seriyi adlandırır,
 *    bu yüzden efsane (legend) KUTUSU ÇİZİLMEZ.
 *  · >= 2 seri: efsane HER ZAMAN vardır — kimlik asla yalnızca renkle taşınmaz.
 *  · TABLO GÖRÜNÜMÜ: her grafiğin yanında gerçek bir <table> alternatifi.
 *    Ekran okuyucu, yazdırma ve renk körlüğü için tek gerçek çözüm budur.
 *  · Boş ve yükleniyor durumları.
 */
import type { ReactNode } from "react";
import { useId, useState } from "react";
import { GRAFIK_RENK } from "./palet.js";

export interface EfsaneOgesi {
  anahtar: string;
  etiket: string;
  renk: string;
}

export interface GrafikOzellikleri {
  baslik: string;
  altBaslik?: string;
  /** >= 2 seri varsa ZORUNLU. Tek seride verilmez. */
  efsane?: EfsaneOgesi[];
  /** Tablo görünümü için satırlar — başlıklar ve hücreler */
  tabloBasliklari: string[];
  tabloSatirlari: (string | number)[][];
  yukleniyor?: boolean;
  bosMesaj?: string;
  children: ReactNode;
  /** Grafiğin ne anlattığını ekran okuyucuya tek cümlede söyler */
  ozet?: string;
}

export function Grafik({
  baslik,
  altBaslik,
  efsane,
  tabloBasliklari,
  tabloSatirlari,
  yukleniyor = false,
  bosMesaj = "Bu aralıkta veri yok.",
  children,
  ozet,
}: GrafikOzellikleri) {
  const [tablo, setTablo] = useState(false);
  const id = useId();
  const bos = tabloSatirlari.length === 0;

  return (
    <figure
      className="m-0 rounded-lg border border-cetvel bg-yuzey"
      aria-labelledby={`${id}-baslik`}
    >
      <figcaption className="flex flex-wrap items-start justify-between gap-3 border-b border-cetvel px-4 py-3">
        <div>
          <h3 id={`${id}-baslik`} className="m-0 text-lg">
            {baslik}
          </h3>
          {altBaslik && (
            <p className="m-0 text-sm text-murekkep-3">{altBaslik}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setTablo((t) => !t)}
          aria-pressed={tablo}
          className="min-h-kontrol rounded-md border border-cetvel bg-girdi px-2.5 text-xs text-murekkep-2 transition-colors duration-defter ease-orta hover:border-cetvel-koyu hover:bg-kagit-2 hover:text-murekkep"
        >
          {tablo ? "Grafiği göster" : "Tablo olarak göster"}
        </button>
      </figcaption>

      {/* Efsane: 2+ seride her zaman. Metin mürekkep tokeninde kalır; renk
          yalnızca yanındaki küçük işaretçide yaşar. */}
      {efsane && efsane.length >= 2 && !tablo && (
        <ul className="m-0 flex flex-wrap gap-x-4 gap-y-1 border-b border-cetvel px-4 py-2 text-sm text-murekkep-2">
          {efsane.map((e) => (
            <li key={e.anahtar} className="flex list-none items-center gap-1.5">
              <span
                aria-hidden="true"
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ background: e.renk }}
              />
              {e.etiket}
            </li>
          ))}
        </ul>
      )}

      <div className="p-4">
        {yukleniyor ? (
          <div
            className="h-56 animate-nabiz rounded-sm bg-kagit-2"
            role="status"
            aria-label="Grafik yükleniyor"
          />
        ) : bos ? (
          <p className="py-12 text-center text-sm text-murekkep-3">
            {bosMesaj}
          </p>
        ) : tablo ? (
          <VeriTablosu basliklar={tabloBasliklari} satirlar={tabloSatirlari} />
        ) : (
          <>
            <div aria-hidden="true">{children}</div>
            {/* Görsel olmayan kullanıcılar için aynı veri, gerçek tablo olarak. */}
            <div className="gizli-erisilebilir">
              {ozet && <p>{ozet}</p>}
              <VeriTablosu
                basliklar={tabloBasliklari}
                satirlar={tabloSatirlari}
              />
            </div>
          </>
        )}
      </div>
    </figure>
  );
}

function VeriTablosu({
  basliklar,
  satirlar,
}: {
  basliklar: string[];
  satirlar: (string | number)[][];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {basliklar.map((b, i) => (
              <th
                key={b}
                scope="col"
                className={`border-b border-cetvel-silik px-3 py-2 text-xs font-bolum text-murekkep-2 ${
                  i === 0 ? "text-left" : "text-right"
                }`}
              >
                {b}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {satirlar.map((s, i) => (
            <tr key={i} className="border-b border-cetvel last:border-0">
              {s.map((h, j) => (
                <td
                  key={j}
                  className={`px-3 py-1.5 ${
                    j === 0 ? "text-left" : "rakam text-right font-mono"
                  }`}
                >
                  {h}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Ortak ipucu (tooltip) gövdesi. Recharts'ın `content` proposuna verilir.
 * Metin mürekkep tokeninde; renk yalnızca işaretçide.
 */
export function Ipucu({
  active,
  payload,
  label,
  birim = "₺",
}: {
  active?: boolean;
  payload?: {
    name?: string;
    value?: unknown;
    color?: string;
    dataKey?: string;
  }[];
  label?: string | number;
  birim?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="animate-kaplama rounded-md border border-cetvel bg-yuzey-2 px-3 py-2 text-sm shadow-2">
      {label !== undefined && (
        <p className="m-0 mb-1 font-satir text-murekkep">{String(label)}</p>
      )}
      {payload.map((p, i) => (
        <p key={i} className="m-0 flex items-center gap-2 text-murekkep-2">
          <span
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ background: p.color ?? GRAFIK_RENK.murekkep3 }}
          />
          <span>{p.name}</span>
          <strong className="rakam ml-auto font-mono text-murekkep">
            {String(p.value)} {birim}
          </strong>
        </p>
      ))}
    </div>
  );
}

/**
 * Kahraman sayı / istatistik karosu.
 * dataviz: "bazen doğru cevap grafik DEĞİLDİR". Tek bir rakamı grafiğe
 * çevirmek bilgi eklemez, gürültü ekler.
 */
export function IstatistikKarosu({
  etiket,
  deger,
  yardimci,
  vurgu = false,
}: {
  etiket: string;
  deger: ReactNode;
  yardimci?: ReactNode;
  vurgu?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border px-4 py-3 ${
        vurgu ? "border-simit-cizgi bg-simit-yumusak" : "border-cetvel bg-yuzey"
      }`}
    >
      <p className="m-0 text-xs text-murekkep-3">{etiket}</p>
      <p className="m-0 mt-0.5 text-2xl font-bolum leading-tight text-murekkep">
        {deger}
      </p>
      {yardimci && <p className="m-0 text-sm text-murekkep-3">{yardimci}</p>}
    </div>
  );
}
